/**
 * Общие детали поля даты и периода: сегменты ввода и окно календаря.
 * Вид — `ac-date*` / `ac-calendar-popover` из analysis-controls.css.
 */
import type { ReactNode } from 'react';
import { Button, Calendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, DateInput, DateSegment, Dialog, Heading, Popover, RangeCalendar } from 'react-aria-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { dateShort } from '../../lib/format';
import { FM } from './messages';

/**
 * Сегменты «день · месяц · год». Пустой сегмент подписан из каталога, а не из
 * словаря библиотеки: тот на узбекской кириллице отдаёт латиницу.
 */
export function DateSegments({ slot }: { slot?: 'start' | 'end' }) {
  const t = useT();
  const hint: Record<string, string> = { day: t(FM.segDay), month: t(FM.segMonth), year: t(FM.segYear) };
  return (
    <DateInput slot={slot}>
      {(segment) => (
        <DateSegment segment={segment} className="ac-date-segment">
          {({ isPlaceholder, type, text }) => (isPlaceholder ? (hint[type] ?? text) : text)}
        </DateSegment>
      )}
    </DateInput>
  );
}

function Header() {
  const t = useT();
  return (
    <header>
      <Button slot="previous" className="ac-calendar-nav" aria-label={t(FM.datePrev)}>
        <ChevronLeft size={18} />
      </Button>
      <Heading />
      <Button slot="next" className="ac-calendar-nav" aria-label={t(FM.dateNext)}>
        <ChevronRight size={18} />
      </Button>
    </header>
  );
}

function Grid() {
  return (
    <CalendarGrid>
      <CalendarGridHeader>{(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}</CalendarGridHeader>
      <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
    </CalendarGrid>
  );
}

/** Окно календаря одной даты. */
export function DateCalendarPopover({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Popover className="ac-popover ac-calendar-popover fm-popover">
      <Dialog aria-label={ariaLabel}>
        <Calendar firstDayOfWeek="mon">
          <Header />
          <Grid />
        </Calendar>
      </Dialog>
    </Popover>
  );
}

/** Окно календаря периода; над сеткой — готовые периоды. */
export function RangeCalendarPopover({ ariaLabel, presets }: { ariaLabel: string; presets?: ReactNode }) {
  return (
    <Popover className="ac-popover ac-calendar-popover fm-popover" placement="bottom start">
      <Dialog aria-label={ariaLabel}>
        {presets}
        <RangeCalendar firstDayOfWeek="mon">
          <Header />
          <Grid />
        </RangeCalendar>
      </Dialog>
    </Popover>
  );
}

/** Граница допустимого окна словами: «5 сен 2026». */
export const boundText = (iso: string) => dateShort(iso);
